/**
 * 레벨 테스트(grade) 세트의 level_value(+학기) → 표시명 i18n 키.
 * level_value 계약(quiz_set_seed): '0'=미취학, '1'~'6'=초등 N학년, '7'~'9'=중등 (N-6)학년.
 * semester(1·2)는 학기로 나뉜 세트(quiz_set_seed/05_semester_sets.sql)에만 있다. 초1·초2는 학기 없음.
 * 키는 admin 네임스페이스 `diagnosticSets.*` 하위. 호출부가 네임스페이스 접두를 붙인다.
 */
export type LevelSetNameKey =
  | 'preschoolLevelSetName'
  | 'gradeSetName'
  | 'gradeSemesterSetName'
  | 'middleGradeSetName'
  | 'middleGradeSemesterSetName'

export interface LevelSetName {
  key: LevelSetNameKey
  params?: { grade: number; semester?: number }
}

const MIDDLE_OFFSET = 6

function validSemester(semester: number | null | undefined): number | null {
  return semester === 1 || semester === 2 ? semester : null
}

export function levelSetName(levelValue: string, semester?: number | null): LevelSetName | null {
  const trimmed = levelValue.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const value = Number(trimmed)
  const term = validSemester(semester)
  if (value === 0) return { key: 'preschoolLevelSetName' }
  if (value >= 1 && value <= 6) {
    return term
      ? { key: 'gradeSemesterSetName', params: { grade: value, semester: term } }
      : { key: 'gradeSetName', params: { grade: value } }
  }
  if (value >= 7 && value <= 9) {
    const grade = value - MIDDLE_OFFSET
    return term
      ? { key: 'middleGradeSemesterSetName', params: { grade, semester: term } }
      : { key: 'middleGradeSetName', params: { grade } }
  }
  return null
}

/** 미취학 레벨 테스트 세트(grade, level_value '0'). 취학 전 유아 평가 금지로 LC LMS 전역에서 숨긴다. */
export function isPreschoolLevelSet(set: { levelType?: string | null; levelValue?: string | null }): boolean {
  return set.levelType === 'grade' && set.levelValue?.trim() === '0'
}

/** 레벨 테스트 세트 정렬: 학년 → 학기(학기 없음 먼저). 같은 학년의 학기 세트가 sort_order 동률이라 화면에서 맞춘다. */
export function compareLevelSets(
  a: { levelValue?: string | null; semester?: number | null },
  b: { levelValue?: string | null; semester?: number | null },
): number {
  const grade = (s: { levelValue?: string | null }) => {
    const n = Number(s.levelValue?.trim())
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER
  }
  return grade(a) - grade(b) || (validSemester(a.semester) ?? 0) - (validSemester(b.semester) ?? 0)
}

/** 인쇄 파일명 등 i18n 없이 쓰는 한글 학년 라벨. 매핑 불가 시 null. 파일명 한 구간이라 공백을 넣지 않는다. */
export function levelSetGradeLabelKo(levelValue: string | null | undefined, semester?: number | null): string | null {
  if (!levelValue?.trim()) return null
  const name = levelSetName(levelValue, semester)
  if (!name) return null
  if (name.key === 'preschoolLevelSetName') return '미취학'
  const term = name.params?.semester ? `${name.params.semester}학기` : ''
  if (name.key === 'gradeSetName' || name.key === 'gradeSemesterSetName') return `초${name.params?.grade}학년${term}`
  return `중${name.params?.grade}학년${term}`
}
